/**
 * support-form-router.js
 *
 * Reindirizza l'invio del form di supporto (sezione Debutify "Contact form builder")
 * verso una function serverless che invia la mail con destinatario custom e CC
 * condizionale in base al prodotto selezionato — cosa NON possibile con la
 * contact form nativa di Shopify ({% form 'contact' %}).
 *
 * Funzionamento:
 *  - Il validator Debutify (dbtfy-form-validator.js) valida i campi e, se tutto ok,
 *    chiama `form.submit()` (submit PROGRAMMATICO: non fa scattare l'evento "submit").
 *  - Qui sovrascriviamo `form.submit` sul form di supporto: al posto della POST nativa
 *    a /contact, parte una fetch verso l'endpoint. La validazione Debutify resta invariata.
 *
 * Lo script si auto-limita al solo form di supporto: il form dentro la sezione
 * "dbtfy_contact_form_builder" che contiene il select dei prodotti (option
 * Smart/Bike/One — nomi a glossario, invarianti in ogni lingua). I name dei campi
 * derivano dalle LABEL TRADOTTE (contact[prodotto] / contact[product] / …), quindi
 * la detection non può basarsi sui name. La mappa Prodotto+Paese→CC vive lato
 * server: qui inviamo prodotto + meta (nome, oggetto, problema, uid, lingua, paese).
 */
(function () {
  'use strict';

  // Endpoint della function serverless. Override possibile da theme.liquid via
  // window.SUPPORT_FORM_ENDPOINT (es. settings_schema) senza toccare questo file.
  var ENDPOINT =
    (typeof window !== 'undefined' && window.SUPPORT_FORM_ENDPOINT) ||
    'https://ticket.trackting.com/api/support';

  var HONEYPOT_NAME = 'company_website';
  var SECTION_SELECTOR = '[id*="dbtfy_contact_form_builder"]';
  var PRODUCT_OPTIONS = ['Smart', 'Bike', 'One']; // glossario: mai tradotti
  var CAPTCHA_RE = /captcha/i; // h-captcha-response, g-recaptcha-response, …

  // Messaggi per l'utente: iniettati da theme.liquid nella lingua corrente
  // (window.SUPPORT_FORM_I18N, chiavi locales mkt.support_form); fallback italiano.
  var TXT = Object.assign(
    {
      sending: 'Invio in corso…',
      success: 'Richiesta inviata correttamente. Ti ricontatteremo al più presto.',
      error: 'Si è verificato un errore durante l’invio. Riprova tra qualche istante.'
    },
    (typeof window !== 'undefined' && window.SUPPORT_FORM_I18N) || {}
  );

  // Paese/lingua correnti, iniettati da theme.liquid (per il routing CC lato server).
  var CONTEXT = (typeof window !== 'undefined' && window.SUPPORT_FORM_CONTEXT) || {};

  /** Estrae un'etichetta leggibile per un campo (stessa logica di dbtfy-form-validator). */
  function labelFor(el) {
    var label = '';
    var container =
      el.closest('.field') || el.closest('.checkbox_field') || el.closest('.field-other');
    if (container) {
      var lbl = container.querySelector('label.field__label') || container.querySelector('label');
      if (lbl) label = lbl.textContent.trim();
    }
    if (!label && el.tagName === 'SELECT') {
      var first = el.options[0];
      if (first && !first.value) label = first.textContent.trim().replace(/^Select\s+/i, '');
    }
    if (!label) {
      label = el.placeholder || el.name || '';
      label = label.replace(/^contact\[(.*)\]$/, '$1').replace(/[_-]/g, ' ');
    }
    return label.replace(/\s*\*\s*$/, '').trim();
  }

  /** True per i campi del captcha (Shopify hCaptcha & simili): mai nel ticket. */
  function isCaptchaField(el) {
    if (CAPTCHA_RE.test(el.name || '') || CAPTCHA_RE.test(el.id || '')) return true;
    return !!(el.closest && el.closest('.h-captcha, .g-recaptcha, [class*="captcha"]'));
  }

  /** Raccoglie i campi visibili compilati in [{ label, value }]. */
  function collectFields(form) {
    var els = form.querySelectorAll('input:not([type="hidden"]), textarea, select');
    var out = [];
    Array.prototype.forEach.call(els, function (el) {
      if (el.name === HONEYPOT_NAME) return;
      if (isCaptchaField(el)) return;
      var type = el.type;
      if ((type === 'checkbox' || type === 'radio') && !el.checked) return;
      var value = (el.value || '').trim();
      if (!value) return;
      out.push({ label: labelFor(el), value: value });
    });
    return out;
  }

  function getButton(form) {
    return form.querySelector("button[type='submit'], [type='submit']");
  }

  function showError(form, message) {
    var box = form.querySelector('.form__error-message');
    if (box) {
      var span = box.querySelector('.error');
      if (span) span.textContent = message;
      box.classList.remove('hidden');
      var heading = box.querySelector('[autofocus], h2');
      if (heading && typeof heading.focus === 'function') heading.focus();
    } else {
      // Fallback se il markup d'errore Debutify non è presente.
      window.alert(message);
    }
  }

  function clearError(form) {
    var box = form.querySelector('.form__error-message');
    if (box) box.classList.add('hidden');
  }

  function showSuccess(form) {
    var wrapper = form.closest('dbtfy-form-validator') || form.parentNode;
    var msg = document.createElement('div');
    msg.className = 'form__message';
    msg.setAttribute('data-support-form-success', '');
    var h2 = document.createElement('h2');
    h2.className = 'form-status form-status-list form__message';
    h2.setAttribute('tabindex', '-1');
    h2.textContent = TXT.success;
    msg.appendChild(h2);
    if (wrapper && wrapper.parentNode) {
      wrapper.parentNode.insertBefore(msg, wrapper);
    }
    form.hidden = true;
    if (typeof h2.focus === 'function') h2.focus();
  }

  function setSending(form, sending) {
    var btn = getButton(form);
    if (!btn) return;
    if (sending) {
      btn.dataset.originalText = btn.dataset.originalText || btn.textContent;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = TXT.sending;
    } else {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
      if (btn.dataset.originalText) btn.textContent = btn.dataset.originalText;
    }
  }

  /** Select del prodotto: l'unico con le option a glossario Smart/Bike/One. */
  function findProductSelect(form) {
    var selects = form.querySelectorAll('select');
    for (var i = 0; i < selects.length; i++) {
      var values = Array.prototype.map.call(selects[i].options, function (o) {
        return (o.value || '').trim();
      });
      var ok = PRODUCT_OPTIONS.every(function (name) {
        return values.indexOf(name) !== -1;
      });
      if (ok) return selects[i];
    }
    return null;
  }

  /** Select del problema: il primo select diverso da quello del prodotto. */
  function findIssueSelect(form, productEl) {
    var selects = form.querySelectorAll('select');
    for (var i = 0; i < selects.length; i++) {
      if (selects[i] !== productEl) return selects[i];
    }
    return null;
  }

  /** Nome del cliente: primo input testuale che non sia lo UID né l'honeypot. */
  function findNameInput(form) {
    var inputs = form.querySelectorAll('input[type="text"]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (el.name === HONEYPOT_NAME || el.name === 'contact[uid]') continue;
      if (isCaptchaField(el)) continue;
      return el;
    }
    return null;
  }

  /**
   * Oggetto della richiesta: primo input testuale che non sia il nome, lo UID,
   * l'honeypot o un campo captcha. Il name handleizzato cambia con la lingua
   * (contact[oggetto]/contact[subject]/…), quindi NON ci si basa su di esso —
   * stessa logica di findNameInput. Nel form l'Oggetto segue il Nome.
   */
  function findSubjectInput(form, nameEl) {
    var inputs = form.querySelectorAll('input[type="text"]');
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (el === nameEl) continue;
      if (el.name === HONEYPOT_NAME || el.name === 'contact[uid]') continue;
      if (isCaptchaField(el)) continue;
      return el;
    }
    return null;
  }

  /** Invio effettivo verso la function serverless. */
  function routeSubmit(form) {
    if (form.dataset.supportSending === 'true') return; // guard anti doppio invio
    form.dataset.supportSending = 'true';

    clearError(form);
    setSending(form, true);

    var productEl = findProductSelect(form);
    var issueEl = findIssueSelect(form, productEl);
    var nameEl = findNameInput(form);
    var subjectEl = findSubjectInput(form, nameEl);
    var uidEl = form.querySelector('input[name="contact[uid]"]');
    var emailEl = form.querySelector('input[type="email"]');
    var honeypotEl = form.querySelector('[name="' + HONEYPOT_NAME + '"]');

    var payload = {
      product: productEl ? (productEl.value || '').trim() : '',
      email: emailEl ? (emailEl.value || '').trim() : '',
      honeypot: honeypotEl ? honeypotEl.value : '',
      fields: collectFields(form),
      // meta espliciti, indipendenti dalla lingua delle label (v. api/support.js)
      meta: {
        name: nameEl ? (nameEl.value || '').trim() : '',
        subject: subjectEl ? (subjectEl.value || '').trim() : '',
        issue: issueEl ? (issueEl.value || '').trim() : '',
        uid: uidEl ? (uidEl.value || '').trim() : '',
        locale: CONTEXT.locale || document.documentElement.lang || '',
        country: CONTEXT.country || ''
      },
      page: window.location.href
    };

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json().catch(function () {
          return {};
        });
      })
      .then(function (data) {
        if (data && data.ok === false) throw new Error(data.error || 'send failed');
        showSuccess(form);
      })
      .catch(function () {
        setSending(form, false);
        showError(form, TXT.error);
        form.dataset.supportSending = 'false';
      });
  }

  /** Aggiunge il campo honeypot nascosto (anti-bot). */
  function injectHoneypot(form) {
    if (form.querySelector('[name="' + HONEYPOT_NAME + '"]')) return;
    var wrap = document.createElement('div');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.position = 'absolute';
    wrap.style.left = '-9999px';
    wrap.style.width = '1px';
    wrap.style.height = '1px';
    wrap.style.overflow = 'hidden';
    var hp = document.createElement('input');
    hp.type = 'text';
    hp.name = HONEYPOT_NAME;
    hp.tabIndex = -1;
    hp.autocomplete = 'off';
    wrap.appendChild(hp);
    form.appendChild(wrap);
  }

  /**
   * Individua il solo form di supporto, in modo indipendente dalla lingua:
   * il form nella sezione Contact Form Builder che contiene il select dei
   * prodotti (option a glossario) e almeno un secondo select (problema).
   */
  function findSupportForm() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) {
      var forms = sections[i].querySelectorAll('form');
      for (var j = 0; j < forms.length; j++) {
        var f = forms[j];
        var productEl = findProductSelect(f);
        if (productEl && findIssueSelect(f, productEl)) return f;
      }
    }
    return null;
  }

  function setup() {
    var form = findSupportForm();
    if (!form || form.dataset.supportRouterReady === 'true') return;
    form.dataset.supportRouterReady = 'true';

    injectHoneypot(form);

    // Sostituisce la consegna nativa: Debutify chiama form.submit() solo dopo
    // che la validazione è passata → qui parte la nostra fetch.
    form.submit = function () {
      routeSubmit(form);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
  // Sicurezza extra nel caso la sezione venga renderizzata dopo (theme editor).
  window.addEventListener('load', setup);
})();
